import { FC, ReactNode, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../context/searchContext";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { InstallationTypeID } from "../../../../../http/vertigrip/types/installationTypes";
import { useInstallationTypesApi } from "../../../../../http/vertigrip/useInstallationTypes";
import FlexContent from "../../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../../ui/Header";
import Tabs, { Tab } from "../../../../ui/Tabs";
import { VERTIGRIP } from "../../AdminSystemSettings";
import InstallationTypeIntermediateElements from "../intermediateElements/InstallationTypeIntermediateElements";
import LowerElements from "../lowerElements/InstallationTypeLowerElements";
import InstallationTypeUpperExtensions from "../upperExtensions/InstallationTypeUpperExtensions";
import InstallationTypeDetails from "./InstallationTypeDetails";
import LadderReinforcements from "./ladderReinforcements/LadderReinforcements";
import WallMaterials from "./wallMaterials/WallMaterials";

const InstallationType: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { installationTypeId } = useParams<{ installationTypeId: InstallationTypeID }>();
  const installationTypeApi = useInstallationTypesApi();
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  const aborter = useRef(new AbortController());
  const installationTypeSwr = installationTypeApi.getAdminInstallationType(aborter.current.signal);

  useEffect(() => {
    installationTypeId && installationTypeSwr.trigger(installationTypeId);
  }, [installationTypeId]);

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData },
    { title: i18n.translation.upperExtensions.plural },
    { title: i18n.translation.lowerElements.plural },
    { title: i18n.translation.intermediateElements.plural },
  ];

  const contents: ReactNode[] = [
    <FlexContent padding direction="row">
      <InstallationTypeDetails
        installationType={!installationTypeSwr.error ? installationTypeSwr.data : undefined}
        disabled={installationTypeSwr.isMutating}
      />
    </FlexContent>,
    <InstallationTypeUpperExtensions active={selectedIndex === 1} />,
    <LowerElements active={selectedIndex === 2} />,
    <InstallationTypeIntermediateElements active={selectedIndex === 3} />,
  ];

  if (installationTypeId === "on-wall") {
    tabs.push({ title: i18n.translation.wallMaterials.plural });
    contents.push(<WallMaterials active={selectedIndex === 4} />);
  }
  if (installationTypeId === "on-ladder") {
    tabs.push({ title: i18n.translation.ladderReinforcements.plural });
    contents.push(<LadderReinforcements active={selectedIndex === 4} />);
  }

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: VERTIGRIP },
    {
      url: `/admin/systems/${systemId}/settings?tab=2`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.installationTypes.plural}`,
    },
    { display: installationTypeSwr.data?.name ?? "" },
  ];

  return (
    <SearchContextProvider>
      <InstallationTypeContent
        title={installationTypeSwr.data?.name ?? ""}
        error={installationTypeSwr.error}
        path={path}
        tabs={tabs}
        contents={contents}
      />
    </SearchContextProvider>
  );
};

type InstallationTypeContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const InstallationTypeContent: FC<InstallationTypeContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="tab" tabs={tabs} contents={contents} />
    </>
  );
};

export default InstallationType;
